/**
 * InlineStaySearch — Batch 2 (2026-08-07 v2)
 *
 * Traveler-facing inline search + results on /explore.
 *
 * Restored from the previous generic build to reuse the original
 * TravelBio card + swipe design:
 *   • `StayCard` + `StaySwipeDeck` from
 *     `/app/frontend/src/components/hotels/StayCards.jsx` — extracted
 *     verbatim from `pages/hotels/HotelsResultsPage.jsx` where they
 *     shipped in the Hotelbeds era.
 *   • `OccupancyStepper` +/- pattern from
 *     `pages/hotels/HotelsSearchPage.jsx` — 6+ cap on both adults and
 *     children per amendment spec.
 *
 * Behavior:
 *   • Dates optional. Empty → today+30 / today+33 (matches Atlas strip).
 *   • Guests popover: Adults 1–5 numeric + "6+" cap; Children 0–5
 *     numeric + "6+" cap. Collapsed label with grammatical pluralize.
 *   • Search runs inline via POST /accommodations/search — no route
 *     change. Deep-link clicks use the delayed-open pattern so the
 *     Stay22 `campaign=<id>` param is baked in before navigation.
 *   • Bermuda pricing fix preserved (backend sends getPrice=true).
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  MapPin, Calendar, Users, Search, ChevronDown,
  Grid3x3, Layers, Loader2, RefreshCcw, Plus, Minus,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  TravelerStayDeck, TravelerStayGrid,
} from "@/components/travelbio/TravelerStayCards";

const _MAX_RETRIES = 3;
const _CAP = 6;   // display "6+" for anything ≥ 6

function todayPlus(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function labelCount(n, singular, plural) {
  if (n >= _CAP) return `6+ ${plural}`;
  if (n === 1)   return `1 ${singular}`;
  return `${n} ${plural}`;
}

function guestsLabel(adults, children) {
  const a = labelCount(adults, "adult", "adults");
  if (children === 0) return a;
  const c = labelCount(children, "child", "children");
  return `${a} · ${c}`;
}


/**
 * Stay22 AccommodationOffer → normalized card shape.
 * (Legacy adapter — traveler cards consume the raw offer directly
 * now via TravelerStayCards, so this helper is unused. Kept for
 * potential future reuse; harmless dead code.)
 */
function _offerToCard(o) {
  const cheapest = o.supplier_offers?.[0];
  const price = typeof cheapest?.price === "number" ? cheapest.price : null;
  const currency = cheapest?.currency || o.currency || null;
  const dest = o.coords ? "" : null;   // Stay22 doesn't ship a destinationName; leave blank
  return {
    code: o.property_id,
    name: o.name,
    thumbnailUrl: o.image || null,
    stars: 0,
    minRate: price,
    currency,
    board: null,
    cancel: null,
    zoneName: null,
    destinationName: dest,
    // Passthroughs for the click handler
    _offer: o,
    _supplier: cheapest,
  };
}


function OccupancyStepper({ label, sublabel, value, onChange, min, max, testId }) {
  const display = value >= _CAP ? "6+" : String(value);
  return (
    <div className="flex items-center justify-between py-2">
      <div>
        <div className="font-hand text-tb-navy text-sm">{label}</div>
        {sublabel && (
          <div className="font-hand text-tb-navy/50 text-[11px]">{sublabel}</div>
        )}
      </div>
      <div className="inline-flex items-center gap-2">
        <button
          type="button"
          data-testid={`${testId}-dec`}
          onClick={() => onChange(Math.max(min, value - 1))}
          disabled={value <= min}
          className="w-8 h-8 rounded-full border-2 border-tb-navy/30 text-tb-navy disabled:opacity-40 flex items-center justify-center tb-tap hover:border-tb-navy"
        >
          <Minus className="w-3.5 h-3.5" />
        </button>
        <span
          data-testid={`${testId}-value`}
          className="min-w-[1.75rem] text-center font-hand text-tb-navy text-base"
        >
          {display}
        </span>
        <button
          type="button"
          data-testid={`${testId}-inc`}
          onClick={() => onChange(Math.min(max, value + 1))}
          disabled={value >= max}
          className="w-8 h-8 rounded-full border-2 border-tb-navy/30 text-tb-navy disabled:opacity-40 flex items-center justify-center tb-tap hover:border-tb-navy"
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}


function logClick(offer, supplier) {
  let tab = null;
  try { tab = window.open("about:blank", "_blank"); } catch {}
  return api.post("/accommodations/click", {
    property_id:   offer.property_id,
    property_name: offer.name,
    supplier:      supplier.supplier,
    supplier_code: supplier.supplier_code,
    price:         supplier.price,
    currency:      supplier.currency,
    deeplink_used: supplier.deeplink_url,
  }).then(({ data }) => {
    const url = data?.url || supplier.deeplink_url;
    if (tab && !tab.closed) {
      try { tab.location.href = url; } catch {}
    } else if (url) {
      window.open(url, "_blank");
    }
  }).catch(() => {
    if (tab && !tab.closed) {
      try { tab.location.href = supplier.deeplink_url; } catch {}
    }
  });
}


export default function InlineStaySearch({ interstitial = null, filters = null }) {
  const [location, setLocation] = useState("");
  const [checkIn, setCheckIn]   = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [adults, setAdults]     = useState(2);
  const [children, setChildren] = useState(0);
  const [guestsOpen, setGuestsOpen] = useState(false);
  const guestsBtnRef = useRef(null);
  const guestsPopRef = useRef(null);

  const [loading, setLoading]   = useState(false);
  const [offers, setOffers]     = useState([]);
  const [error, setError]       = useState(null);
  const [rateLimited, setRateLimited] = useState(false);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const [view, setView]         = useState("grid");
  const [meta, setMeta]         = useState({ cache_hit: false });

  // Close guests popover on outside click
  useEffect(() => {
    if (!guestsOpen) return;
    const onDoc = (e) => {
      if (guestsPopRef.current?.contains(e.target)) return;
      if (guestsBtnRef.current?.contains(e.target)) return;
      setGuestsOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [guestsOpen]);

  const doSearch = useCallback(async (attempt = 0) => {
    if (!location.trim()) {
      setError("Enter a city or country to search.");
      return;
    }
    setLoading(true); setError(null); setRateLimited(false);
    // Item B (2026-08-08) — Explore skip/save persistence.
    // Fetch the user's currently-actioned (skip + save) property_ids
    // for this location so they don't reappear in the results.
    // Silent on failure: an empty list just means "show everything",
    // which is the pre-Item-B behaviour.
    let excludeIds = [];
    try {
      const { data: sw } = await api.get(
        `/swipes?location=${encodeURIComponent(location.trim())}`);
      excludeIds = Array.isArray(sw?.property_ids) ? sw.property_ids : [];
    } catch {}

    // Stay22 backend requires getPrice=true AND explicit check dates
    // for prices to come back. Frontend default when both empty:
    // today+30 / today+33 (mirrors the Atlas-strip default so both
    // callers share the same cache bucket).
    const payload = {
      location: location.trim(),
      guests: {
        adults:   Math.min(adults, _CAP),
        // Stay22 accepts a `children` count via array length; ages
        // are optional at this endpoint (verified 2026-08-07 direct
        // probe). We pad with zeros so the array length signals the
        // count; if a future endpoint requires ages we'll default to
        // 8 per HotelsSearchPage's convention.
        children: Array(Math.min(children, _CAP)).fill(0),
      },
      page_size: 30,
      exclude_property_ids: excludeIds,
    };
    // Symptom 3.2 (2026-08-08) — forward propertyTypes chip picks so
    // the backend can hard-filter. Without this the chips render but
    // never affect the result set.
    const chipTypes = Array.isArray(filters?.propertyTypes) ? filters.propertyTypes : [];
    if (chipTypes.length > 0) {
      payload.filters = { property_types: chipTypes };
    }
    payload.check_in  = checkIn  || todayPlus(30);
    payload.check_out = checkOut || todayPlus(33);
    try {
      const { data } = await api.post("/accommodations/search", payload);
      if (data?.rate_limited) {
        setRateLimited(true); setOffers([]);
        if (attempt < _MAX_RETRIES) {
          const wait = Math.min(Math.max(500, Number(data.retry_after_ms || 800)), 5000);
          setRetryAttempt(attempt + 1);
          setTimeout(() => doSearch(attempt + 1), wait);
        }
        return;
      }
      setOffers(data?.offers || []);
      setMeta({
        cache_hit: !!data?.cache_hit,
        total_before_exclusion: data?.total_before_exclusion ?? (data?.offers?.length || 0),
        excluded_count: data?.excluded_count ?? 0,
      });
      setRetryAttempt(0);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || "Search failed. Try again.");
    } finally {
      setLoading(false);
    }
  }, [location, checkIn, checkOut, adults, children, filters?.propertyTypes]);

  const submit = (e) => { e.preventDefault(); doSearch(0); };

  // Symptom 3.2 (2026-08-08) — Auto re-run search whenever the
  // filter chips change AND the user has already run at least one
  // search (offers non-empty OR loading in progress). Prevents
  // premature searches before the user picks a destination.
  const chipTypesKey = (filters?.propertyTypes || []).slice().sort().join("|");
  const hasSearched = useRef(false);
  useEffect(() => {
    if (offers.length > 0) hasSearched.current = true;
  }, [offers.length]);
  useEffect(() => {
    if (!hasSearched.current || !location.trim()) return;
    doSearch(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chipTypesKey]);

  // Item B — "Show them again" flow. Clears only skip rows for the
  // current location; saved (hearted) properties stay filtered
  // because they're also in bookmarks_v2.
  const showAllAgain = useCallback(async () => {
    if (!location.trim()) return;
    try {
      await api.delete(
        `/swipes?location=${encodeURIComponent(location.trim())}&action=skip`);
    } catch {}
    doSearch(0);
  }, [location, doSearch]);

  return (
    <div data-testid="explore-inline-search" className="space-y-6">
      {/* ── search card ── */}
      <form
        onSubmit={submit}
        data-testid="explore-inline-search-form"
        className="rounded-3xl p-5 md:p-6 shadow-[0_18px_40px_-24px_rgba(0,0,0,0.5)]"
        style={{ backgroundColor: "#0f2740" }}
      >
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.15em] text-white/50 font-hand">Where to</span>
          <div className="mt-1.5 flex items-center gap-2 rounded-xl bg-white/[0.06] border border-white/15 h-11 px-3">
            <MapPin className="w-4 h-4 text-white/60" />
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Search by city or country"
              data-testid="explore-inline-location"
              className="bg-transparent flex-1 text-white placeholder:text-white/45 font-hand outline-none"
            />
          </div>
        </label>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-3 mt-3">
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.15em] text-white/50 font-hand">Check-in</span>
            <div className="mt-1.5 flex items-center gap-2 rounded-xl bg-white/[0.06] border border-white/15 h-11 px-3">
              <Calendar className="w-4 h-4 text-white/60 shrink-0" />
              <input
                type="date"
                value={checkIn}
                onChange={(e) => setCheckIn(e.target.value)}
                data-testid="explore-inline-checkin"
                className="bg-transparent flex-1 text-white placeholder:text-white/45 font-hand outline-none w-full [color-scheme:dark]"
              />
            </div>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.15em] text-white/50 font-hand">Check-out</span>
            <div className="mt-1.5 flex items-center gap-2 rounded-xl bg-white/[0.06] border border-white/15 h-11 px-3">
              <Calendar className="w-4 h-4 text-white/60 shrink-0" />
              <input
                type="date"
                value={checkOut}
                onChange={(e) => setCheckOut(e.target.value)}
                data-testid="explore-inline-checkout"
                className="bg-transparent flex-1 text-white placeholder:text-white/45 font-hand outline-none w-full [color-scheme:dark]"
              />
            </div>
          </label>
          <div className="block relative">
            <span className="text-[10px] uppercase tracking-[0.15em] text-white/50 font-hand">Guests</span>
            <button
              type="button"
              ref={guestsBtnRef}
              onClick={() => setGuestsOpen((v) => !v)}
              data-testid="explore-inline-guests-toggle"
              className="mt-1.5 flex items-center gap-2 rounded-xl bg-white/[0.06] border border-white/15 h-11 px-3 w-full text-left"
            >
              <Users className="w-4 h-4 text-white/60 shrink-0" />
              <span
                data-testid="explore-inline-guests-label"
                className="flex-1 text-white font-hand text-sm"
              >
                {guestsLabel(adults, children)}
              </span>
              <ChevronDown className={`w-4 h-4 text-white/60 transition-transform ${guestsOpen ? "rotate-180" : ""}`} />
            </button>
            {guestsOpen && (
              <div
                ref={guestsPopRef}
                data-testid="explore-inline-guests-popover"
                className="absolute right-0 top-full mt-2 z-30 w-64 rounded-2xl bg-white border-2 border-tb-navy/15 shadow-xl p-3"
              >
                <OccupancyStepper
                  label="Adults"
                  sublabel="Ages 18+"
                  value={adults}
                  onChange={setAdults}
                  min={1}
                  max={_CAP}
                  testId="explore-inline-adults"
                />
                <div className="h-px bg-tb-navy/10" />
                <OccupancyStepper
                  label="Children"
                  sublabel="Ages 0-17"
                  value={children}
                  onChange={setChildren}
                  min={0}
                  max={_CAP}
                  testId="explore-inline-children"
                />
                <button
                  type="button"
                  onClick={() => setGuestsOpen(false)}
                  data-testid="explore-inline-guests-done"
                  className="mt-2 w-full h-9 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-xs"
                >
                  Done
                </button>
              </div>
            )}
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          data-testid="explore-inline-submit"
          className="mt-4 w-full h-12 rounded-full bg-tb-gold-bright text-tb-navy font-hand text-sm font-semibold disabled:opacity-60 inline-flex items-center justify-center gap-2 hover:brightness-110"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          Search
        </button>

        {/* Disclaimer — immediately below the Search button, low
            emphasis. Muted-white to sit on the dark-navy card. */}
        <p
          data-testid="explore-inline-disclaimer"
          className="mt-3 text-center text-[11px] font-hand text-white/50 leading-relaxed"
        >
          TravelBio may earn a commission when you book through our links.
        </p>
      </form>

      {/* Filters slot — rendered by the parent (Explore) between the
          search card and the result-meta row so the Filters pill
          sits directly under the search bar as it did pre-Stay22.
          Kept as an opt-in prop so other callers (e.g. Atlas)
          continue to work without it. */}
      {interstitial}

      {/* result meta + view toggle */}
      {(offers.length > 0 || loading || rateLimited || error) && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-xs text-tb-navy/60 font-hand" data-testid="stay-result-meta">
            {loading && "Checking live availability…"}
            {!loading && offers.length > 0 && (
              <>
                {offers.length} stays
                {meta.cache_hit && <span className="ml-2 opacity-60">· cached</span>}
              </>
            )}
          </div>
          {offers.length > 0 && (
            <div
              role="tablist"
              data-testid="stay-view-toggle"
              className="inline-flex rounded-full border border-tb-navy/20 bg-white overflow-hidden text-xs"
            >
              <button
                type="button"
                onClick={() => setView("grid")}
                data-testid="stay-view-grid"
                className={`px-3 h-8 inline-flex items-center gap-1 font-hand ${view === "grid" ? "bg-tb-navy text-tb-gold-bright" : "text-tb-navy hover:bg-tb-navy/5"}`}
              >
                <Grid3x3 className="w-3.5 h-3.5" /> Grid
              </button>
              <button
                type="button"
                onClick={() => setView("swipe")}
                data-testid="stay-view-swipe"
                className={`px-3 h-8 inline-flex items-center gap-1 font-hand ${view === "swipe" ? "bg-tb-navy text-tb-gold-bright" : "text-tb-navy hover:bg-tb-navy/5"}`}
              >
                <Layers className="w-3.5 h-3.5" /> Swipe
              </button>
            </div>
          )}
        </div>
      )}

      {rateLimited && (
        <div
          data-testid="stay-rate-limited"
          className="rounded-xl border-2 border-amber-300 bg-amber-50/60 p-4 flex items-center justify-between gap-3"
        >
          <div className="text-sm text-amber-900 font-hand">
            {retryAttempt < _MAX_RETRIES
              ? <>Checking availability… one moment (retry {retryAttempt}/{_MAX_RETRIES}).</>
              : <>Live availability temporarily unavailable. Try again in a minute.</>}
          </div>
          <button
            type="button"
            onClick={() => doSearch(0)}
            data-testid="stay-manual-retry"
            className="h-9 px-3 rounded-full border-2 border-amber-600 text-amber-900 font-hand text-xs inline-flex items-center gap-1.5"
          >
            <RefreshCcw className="w-3.5 h-3.5" /> Retry
          </button>
        </div>
      )}

      {error && !rateLimited && (
        <div data-testid="stay-error" className="text-sm text-red-700 font-hand p-4 rounded-xl border-2 border-red-200 bg-red-50">
          {error}
        </div>
      )}

      {loading && offers.length === 0 && !rateLimited && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="stay-skeleton">
          {[1,2,3,4,5,6].map(i => (
            <div key={i} className="rounded-2xl border-2 border-tb-navy/10 h-72 bg-tb-navy/[0.03] animate-pulse" />
          ))}
        </div>
      )}

      {!loading && !rateLimited && offers.length === 0 && !error && location && (
        <div data-testid="stay-empty" className="text-center py-8 text-tb-navy/50 font-hand">
          {meta.excluded_count > 0 ? (
            <>
              <p className="text-base text-tb-navy" data-testid="stay-empty-viewed-title">
                All options for {location} viewed.
              </p>
              <p className="mt-1 text-sm">
                {meta.excluded_count} propert{meta.excluded_count === 1 ? "y" : "ies"} filtered
                from previous swipes. Hearted stays stay in your bookmarks.
              </p>
              <button
                type="button"
                onClick={showAllAgain}
                data-testid="stay-empty-show-again"
                className="mt-4 inline-flex items-center gap-1.5 h-10 px-5 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-sm hover:brightness-110 tb-tap"
              >
                <RefreshCcw className="w-4 h-4" />
                Show them again
              </button>
            </>
          ) : (
            "No stays found. Try a broader location or shift your dates."
          )}
        </div>
      )}

      {/* Results — TravelBio traveler swipe deck + grid.
          Front-face card: photo · description · Undo/Skip/Heart row
          · Book Now. Tap the card body to open the detail modal
          with full supplier list. Reused from
          /app/frontend/src/components/travelbio/TravelerStayCards.jsx */}
      {offers.length > 0 && view === "grid" && (
        <TravelerStayGrid offers={offers} location={location} />
      )}
      {offers.length > 0 && view === "swipe" && (
        <TravelerStayDeck offers={offers} location={location} />
      )}
    </div>
  );
}
